import {
  Button,
  Card,
  Col,
  Input,
  Popover,
  Row,
  Skeleton,
  Space,
  Table,
  Tooltip,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useTheme } from "../../Context/ThemeContext";
import { useClient } from "../../Context/ClientContext";
import { ColumnsType } from "antd/es/table";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { LedgerStatus } from "../../Services/LedgerService";
import InvoiceExportService from "../../Services/InvoiceExportDetails";
import { notificationContext } from "../../Common/PageRoute";
import OrganisationsService, {
  Department_Type,
  DepartmentTypeList,
  IDepttData,
} from "../../Services/Organisations/OrganisationsService";
import AddDepttModel from "./AddDepttModel";
import { OrgDataType } from "./Organisations";
import { IApiResponse } from "../../../Utilities/ApiUtility.axios";
import { IIdNameModel } from "../../Services/ItemsService";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import { IDropdownProps } from "../Settings/SettingVoucherTemplate";

interface DepttDataType extends OrgDataType {
  depttName: string;
  depttType: Department_Type;
  subUnitName: string;
}

const Departments = () => {
  const { currentClient } = useClient();
  const [openDepttModel, setOpenDepttModel] = useState<{
    open: boolean;
    id: string;
  }>({
    open: false,
    id: "",
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    id: string;
  }>({
    open: false,
    id: "",
  });
  const [addUpdateTrigger, setAddUpdateTrigger] = useState<boolean>(false);
  const [searchDeptt, setSearchDeptt] = useState<string>("");
  const { themeData } = useTheme();
  const { openNotification } = useContext(notificationContext);
  const [depttList, setDepttList] = useState<DepttDataType[]>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);

  const [filter, setfilter] = useState<any>({
    sortCol: "",
    sortDir: "",
  });
  // const [exportType, setExportType] = useState<number>(-1);
  // const [exportDepData, setExportDepData] = useState<any[]>([]);

  const getDepttList = async () => {
    setDataLoading(true);
    await OrganisationsService.getDepttList(
      currentClient?.id!,
      searchDeptt,
      "",
      "",
      LedgerStatus.active
    ).then((val: IDepttData[] | null) => {
      setDepttList(
        val?.map<DepttDataType>((deptt: IDepttData, index: number) => {
          return {
            sno: index + 1,
            id: deptt.id as string,
            depttName: deptt.depttName,
            depttType: deptt.depttType,
            orgName: deptt.orgName as string,
            subUnitName: deptt.subUnitName as string,
            status: deptt.status!,
          };
        }) || []
      );
    });
    setDataLoading(false);
  };

  const handleDelete = async (selId: string) => {
    if (selId) {
      await OrganisationsService.deleteDepartment(currentClient?.id!, selId)
        .then((val: IApiResponse<IIdNameModel>) => {
          if (val?.status) {
            openNotification(
              "success",
              val?.result?.name + " department deleted successfully"
            );
            setAddUpdateTrigger((prev) => !prev);
          } else {
            openNotification("error", val?.errors && val?.errors[0]?.message);
          }
        })
        .catch((err) => {
          openNotification("error", err?.message || "");
        });
      setDeleteConfirmation({
        open: false,
        id: "",
      });
    }
  };

  useEffect(() => {
    if (currentClient?.id) {
      getDepttList();
    }
  }, [currentClient?.id, addUpdateTrigger, searchDeptt]);

  // --------- Departments Table Columns  ---------
  const depttCols: ColumnsType<DepttDataType> = [
    {
      title: "S.No.",
      dataIndex: "sno",
      key: "sno",
      width: "5%",
      render: (value: number) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Department's Name",
      dataIndex: "depttName",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Department's Type",
      dataIndex: "depttType",
      render: (value: Department_Type) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {
            DepartmentTypeList.find(
              (deptt: IDropdownProps) => deptt.value === value
            )?.label
          }
        </Skeleton>
      ),
    },
    {
      title: "Sub Unit Name",
      dataIndex: "subUnitName",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Organisation Name",
      dataIndex: "orgName",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Action",
      dataIndex: "id",
      width: "10%",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <Row gutter={18}>
            <Col>
              <Tooltip title="Edit" placement="top" color="#3D4998">
                <EditOutlined
                  style={{
                    color: "#444653",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setOpenDepttModel({
                      open: true,
                      id: value,
                    });
                  }}
                />
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title="Delete" placement="top" color="red">
                <DeleteOutlined
                  style={{
                    color: "red",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setDeleteConfirmation({
                      open: true,
                      id: value,
                    })
                  }
                />
              </Tooltip>
            </Col>
          </Row>
        </Skeleton>
      ),
    },
  ];

  // let path = "Department list";
  // const columnsA = ["Department Name"];

  // const fieldMapping = {
  //   // "S.No.": (item: any, index: number) => index,
  //   "Department Name": (item: any, index: number) => item?.depttName,
  // };

  // const generateFooterRow = (columns: string[]) => {
  //   return columns.map((col) => {});
  // };

  // const exportCsv = (exportData: any[]) => {
  //   InvoiceExportService.generateCSV(
  //     exportData,
  //     columnsA,
  //     fieldMapping,
  //     generateFooterRow,
  //     "",
  //     "",
  //     path,
  //     currentClient?.businessName
  //   );
  //   setExportType(0);
  // };

  // const exportPDF = (exportData: any[]) => {
  //   InvoiceExportService.generatePDF(
  //     exportData,
  //     currentClient?.businessName,
  //     columnsA,
  //     fieldMapping,
  //     generateFooterRow,
  //     "",
  //     path
  //   );
  //   setExportType(0);
  // };

  // const exportExcel = async (exportData: any[]) => {
  //   await InvoiceExportService.generateExcel(
  //     exportData,
  //     currentClient?.businessName,
  //     columnsA,
  //     fieldMapping,
  //     generateFooterRow,
  //     "",
  //     path
  //   );
  //   setExportType(0);
  // };

  // const content = (
  //   <Space direction="vertical">
  //     <div>
  //       <Button
  //         style={{ width: "80px" }}
  //         size="small"
  //         title="CSV"
  //         onClick={() => {
  //           if (exportDepData.length > 0) {
  //             exportCsv(exportDepData);
  //           } else {
  //             setExportType(1);
  //             // setApiCallExports(true);
  //           }
  //         }}
  //         icon={<DownloadOutlined />}
  //       >
  //         CSV
  //       </Button>
  //     </div>

  //     <div>
  //       <Button
  //         style={{ width: "80px" }}
  //         size="small"
  //         title="PDF"
  //         onClick={() => {
  //           if (exportDepData.length > 0) {
  //             exportPDF(exportDepData);
  //           } else {
  //             setExportType(2);
  //             // setApiCallExports(true);
  //           }
  //         }}
  //         icon={<DownloadOutlined />}
  //       >
  //         PDF
  //       </Button>
  //     </div>
  //     <div>
  //       <Button
  //         style={{ width: "80px" }}
  //         size="small"
  //         title="XLSX"
  //         onClick={() => {
  //           if (exportDepData.length > 0) {
  //             exportExcel(exportDepData);
  //           } else {
  //             setExportType(3);
  //             // setApiCallExports(true);
  //           }
  //         }}
  //         icon={<DownloadOutlined />}
  //       >
  //         XLSX
  //       </Button>
  //     </div>
  //   </Space>
  // );

  return (
    <Card
      size="small"
      className="TableCard"
      style={{
        marginTop: 35,
        boxShadow: "0px 0px 10px 0px #96A1C84D",
      }}
    >
      <div>
        <Row justify="space-between">
          <Col>
            <Row gutter={15}>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    setOpenDepttModel({
                      open: true,
                      id: "",
                    })
                  }
                >
                  Department
                </Button>
              </Col>
              {/* <Col>
                {depttList.length === 0 ? (
                  <Button icon={<DownloadOutlined />} disabled type="primary">
                    Export
                  </Button>
                ) : (
                  <Popover
                    content={content}
                    placement="rightTop"
                    trigger="hover"
                  >
                    <Button
                      type="default"
                      icon={<DownloadOutlined />}
                      // onClick={() => exportCsv()}
                    >
                      Export
                    </Button>
                  </Popover>
                )}
              </Col> */}
            </Row>
          </Col>
          <Col>
            <Input
              allowClear
              style={{ width: "220px" }}
              placeholder="Search department"
              onChange={(e: any) => {
                setDataLoading(true);
                if (!e.cancelable) {
                  //console.log("typeing user............");
                  const searchItemGroup = setTimeout(() => {
                    setSearchDeptt(e.target.value);
                  }, 1000);
                  // setExportDepData([]);
                  // setExportType(-1);
                  return () => clearTimeout(searchItemGroup);
                } else {
                  // console.log("Click cancle............");
                  setSearchDeptt(e.target.value);
                }
              }}
              suffix={dataLoading || searchDeptt ? "" : <SearchOutlined />}
            />
          </Col>
        </Row>
        <Table
          loading={dataLoading}
          className={`Tabel-style table-${
            themeData?.componentSize ?? "middle"
          }`}
          style={{ width: "100%", marginTop: "10px" }}
          columns={depttCols}
          dataSource={depttList}
        />
      </div>
      {openDepttModel && (
        <AddDepttModel
          isModalOpen={openDepttModel.open}
          setIsModalOpen={setOpenDepttModel}
          id={openDepttModel.id}
          setTrigger={setAddUpdateTrigger}
        />
      )}
      {deleteConfirmation && (
        <ConfirmationModal
          open={deleteConfirmation.open}
          onNo={() =>
            setDeleteConfirmation({
              open: false,
              id: "",
            })
          }
          onYes={() => handleDelete(deleteConfirmation?.id)}
          text={`Are you sure you want to delete this row?`}
        />
      )}
    </Card>
  );
};

export default Departments;
